import { useEffect, useState, useRef } from 'react';
import { MainTitle } from 'components/MainTitle';
import { TaxesList } from 'components/TaxesList';
import { useGetTaxesQuery } from 'redux/Api/taxesApi/taxesApi';
import { Nothing } from 'components/Nothing';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import styles from './Taxes.module.scss';
import { InputSearch } from 'ui/InputSearch';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { NewSelectedMounth } from 'components/NewSelectedMounth';
import { currentYearNewFormat, getOnlyMounth, listMounth } from 'utils/date';
import { QueryParamsTaxesType, TaxesItemsTypes } from 'redux/Api/taxesApi/type';
import { ReactComponent as IconHide } from '../../assets/img/iconHide.svg';
import { ReactComponent as IconView } from '../../assets/img/iconView.svg';
import { Button } from 'ui/Button';

interface ListMounthType {
  name: string;
  key: number;
}

export const Taxes = () => {
  const [searchValue, setSearchValue] = useState('');
  const [allItems, setAllItems] = useState<TaxesItemsTypes[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(getOnlyMounth());
  const [selectedYear, setSelectedYear] = useState<number>(Number(currentYearNewFormat));
  const [isBlurred, setIsBlurred] = useState(true);
  const prevTaxPeriodRef = useRef<string | undefined>();

  const per_page = 50;

  const initialTaxPeriod = `${selectedYear}-${listMounth.find(
    (elem) => elem.name === selectedMonth,
  )?.key}`;

  const [queryParams, setQueryParams] = useState<QueryParamsTaxesType>({
    page: 1,
    per_page,
    tax_period: initialTaxPeriod,
  });

  if (!prevTaxPeriodRef.current) {
    prevTaxPeriodRef.current = initialTaxPeriod;
  }

  const { data, isLoading } = useGetTaxesQuery(queryParams);
  const total_balance = data?.total;

  useEffect(() => {
    if (data?.items && Array.isArray(data.items)) {
      if (queryParams.page === 1) {
        setAllItems(data.items);
      } else {
        setAllItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.worker_id));
          const newItems = data.items.filter(
            (item: TaxesItemsTypes) => !existingIds.has(item.worker_id),
          );
          return [...prev, ...newItems];
        });
      }
    } else if (data && !data.items) {
      setAllItems([]);
    }
  }, [data, queryParams.page]);

  useEffect(() => {
    const monthKey = listMounth.find((elem) => elem.name === selectedMonth)?.key;

    if (monthKey) {
      const newTaxPeriod = `${selectedYear}-${monthKey.toString().padStart(2, '0')}`;

      if (prevTaxPeriodRef.current !== newTaxPeriod) {
        prevTaxPeriodRef.current = newTaxPeriod;
        setQueryParams((prev) => ({
          ...prev,
          page: 1,
          tax_period: newTaxPeriod,
        }));
        setAllItems([]);
      }
    }
  }, [selectedMonth, selectedYear]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: searchValue || undefined,
      }));
      setAllItems([]);
    }
  };

  const hasNextPage = data?.meta
    ? data?.meta?.last_page !== data?.meta?.current_page
    : false;

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 800px 0px',
  });

  const balanceData = [
    {
      label: 'Общая сумма копилки, ₽',
      value: total_balance?.total_amount_in_taxbox || 0,
    },
    { label: 'Оплачено, ₽', value: total_balance?.total_amount_paid || 0 },
    { label: 'Возвращено, ₽', value: total_balance?.total_amount_returned || 0 },
  ];

  return (
    <div className={styles.root}>
      <MainTitle title='Налоги' count={data?.meta.total} />
      <div className={styles.root_search}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div className={styles.search_input}>
            <InputSearch
              notFocus
              placeholder='Искать'
              value={searchValue}
              onKeyDown={handleKeyDown}
              onSearch={() => {
                setQueryParams((prev) => ({
                  ...prev,
                  page: 1,
                  search: searchValue || undefined,
                }));
                setAllItems([]);
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchValue(e.target.value);

                if (e.target.value === '') {
                  setQueryParams((prev) => ({
                    ...prev,
                    page: 1,
                    search: undefined,
                  }));
                  setAllItems([]);
                }
              }}
            />
          </div>
          <div>
            <NewSelectedMounth
              listMounth={listMounth}
              selectedMonth={listMounth?.find(
                (elem: ListMounthType) => elem.name === selectedMonth,
              )}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              showTotal_amount={false}
            />
          </div>
        </div>
        <div className={styles.balance_grid}>
          {balanceData.map((item, index) => (
            <div key={index} className={styles.balance_item}>
              <p className={styles.label}>{item.label}</p>
              <p
                className={styles.value}
                style={{
                  filter: isBlurred ? 'blur(10px)' : 'none',
                  transition: 'filter 0.2s ease',
                }}>
                {addSpaceNumber(item.value)} ₽
              </p>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <Button
              onClick={() => setIsBlurred(!isBlurred)}
              theme='outline_icon'
              className={styles.balance_button}>
              <div style={{ display: 'flex', alignItems: 'end' }}>
                {isBlurred ? <IconView /> : <IconHide />}
              </div>
            </Button>
          </div>
        </div>
      </div>
      {isLoading ? (
        <LoadingSkeleton mt />
      ) : allItems?.length ? (
        <TaxesList taxesList={allItems} intersectionRef={intersectionRef} />
      ) : (
        <Nothing style={{ height: '750px' }} title='Ничего не найдено' />
      )}
    </div>
  );
};
